import {type Match} from '@wandb/weave/common/types/base';
import {getCookie} from '@wandb/weave/common/util/cookie';
import React, {useEffect, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';

import {useViewer} from '../state/viewer/hooks';
import {
  endPageMetricSession,
  initPageMetricSession,
} from '../util/datadog/artifacts/pageMetricSession';
import {
  setRumViewContextProperty,
  startDatadogRumView,
  usePersistentRumViewContext,
} from '../util/datadog/datadogRum';
import {trackPage} from '../util/navigation';
import {type AppRouteData} from './types';
import {useCurrentRouteMatchParams} from './utils';

const HOST_SESSION_ID_COOKIE = `host_session_id`;

export const getRouteTab = (
  match: Match,
  additionalProps: AppRouteData['additionalProps']
) => {
  if (match?.params?.tab) {
    return match.params.tab;
  }
  if ('tab' in additionalProps) {
    return additionalProps.tab;
  }
  return undefined;
};

/**
 * A lightweight wrapper component that handles analytics and datadog RUM tracking at route level.
 * This should only wrap the component that gets rendered when the path matches because we only want
 * to trigger tracking ONCE.
 */
export const TrackRouteComponent = ({
  children,
  datadogViewName,
  tabParam,
}: {
  children: React.ReactNode;
  datadogViewName: string;
  /** @deprecated to be removed in favor of just `datadogViewName` (with envIsDev validation) */
  tabParam?: string;
}) => {
  const routePerfContext = useMemo(
    () => ({
      routeTrackingId: uuidv4(),
      routeTrackingPerfStart: Date.now(),
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [datadogViewName, tabParam]
  );

  // Identity dimensions for RUM `@context.identity.*`: entity/project come from the
  // route; org is the viewer's enterprise org as a proxy. This can be wrong (a viewer
  // in Org A can view Org B's content), but deriving org from the URL entity needs an
  // async lookup we don't want to block RUM on.
  const {entityName, projectName} = useCurrentRouteMatchParams();
  const viewer = useViewer();
  const orgName = useMemo(
    () =>
      viewer?.organizations?.find(o =>
        o.subscriptions?.some(s => s.subscriptionType === 'ENTERPRISE')
      )?.name,
    [viewer]
  );

  useEffect(() => {
    const tabSuffix = tabParam ? `:${tabParam}` : '';
    const datadogViewNameWithTab = datadogViewName + tabSuffix;

    // `routeName`/`tab` are external analytics-schema keys consumed by segment/
    // mixpanel/perf events, so keep the key names and pass the view name as the value.
    initPageMetricSession(
      datadogViewNameWithTab,
      {routeName: datadogViewName, tab: tabParam},
      {
        uid: routePerfContext.routeTrackingId,
        startTime: routePerfContext.routeTrackingPerfStart,
      }
    );

    trackPage(
      {
        routeName: datadogViewName,
        tab: tabParam,
        routeAndTab: datadogViewNameWithTab,
        isNew: true,
        routeTrackingId: routePerfContext.routeTrackingId,
        routeTrackingPerfStart: routePerfContext.routeTrackingPerfStart,
      },
      {
        context: {
          hostSessionID: getCookie(HOST_SESSION_ID_COOKIE),
        },
      }
    );

    startDatadogRumView(datadogViewNameWithTab);

    // Do not reset RUM view state (timing ledger, backgrounded flag) here:
    // this cleanup runs while the old SDK view is still current, which lets
    // still-mounted timing hooks refire against it. startDatadogRumView resets
    // that state atomically with the next view.
    return () => {
      endPageMetricSession(routePerfContext.routeTrackingId);
    };
  }, [datadogViewName, tabParam, routePerfContext]);

  useEffect(() => {
    setRumViewContextProperty('identity.entity', entityName);
    setRumViewContextProperty('identity.project', projectName);
    setRumViewContextProperty('identity.org', orgName);

    // For backwards compatibility
    setRumViewContextProperty('entityName', entityName);
    setRumViewContextProperty('projectName', projectName);
  }, [entityName, projectName, orgName, datadogViewName, tabParam]);

  return <>{children}</>;
};
